import { easybackClient } from './api'
import { cacheService, CACHE_TTL } from './cacheService'
import { enrollmentService } from './enrollmentService'
import { contentService } from './contentService'
import type { StudentProgress, Subject, Topic, Lesson } from '@/types/index'
import type { StudentEnrollment } from './enrollmentService'

export interface ProgressSummary {
  userId: number
  totalLessons: number
  completedLessons: number
  inProgressLessons: number
  notStartedLessons: number
  overallProgress: number
  totalStudyTime: number
  subjects: SubjectProgress[]
  weakAreas: WeakArea[]
  strongAreas: StrongArea[]
}

export interface SubjectProgress {
  subjectId: number
  subjectName: string
  examType: string
  totalTopics: number
  completedTopics: number
  totalLessons: number
  completedLessons: number
  progressPercentage: number
  averageScore?: number
  totalStudyTime: number
}

export interface TopicProgress {
  topicId: number
  topicName: string
  subjectId: number
  subjectName: string
  totalLessons: number
  completedLessons: number
  progressPercentage: number
  masteryLevel: 'beginner' | 'intermediate' | 'advanced'
}

export interface WeakArea {
  topicId: number
  topicName: string
  subjectName: string
  subjectId: number
  progressPercentage: number
  completedLessons: number
  totalLessons: number
}

export interface StrongArea {
  topicId: number
  topicName: string
  subjectName: string
  subjectId: number
  progressPercentage: number
  completedLessons: number
  totalLessons: number
}

export interface LessonProgressData {
  lessonId: number
  completionPercentage: number
  timeSpent: number
  status: 'not_started' | 'in_progress' | 'completed'
  score?: number
}

const USER_PROGRESS_CACHE_KEY = (userId: number) => `user_progress_${userId}`

export const progressService = {
  getAllUserProgress: async (userId: number): Promise<StudentProgress[]> => {
    const cacheKey = USER_PROGRESS_CACHE_KEY(userId)
    const cached = cacheService.get<StudentProgress[]>(cacheKey)
    if (cached) return cached

    const progressResult = await easybackClient.list<StudentProgress>('student_progress', {
      filter: { user_id: { eq: userId } },
      limit: 1000
    })

    const progressRecords = progressResult.data || []
    cacheService.set(cacheKey, progressRecords, CACHE_TTL.MEDIUM)
    return progressRecords
  },

  calculateLessonProgressFromData: (
    lessonId: number,
    progressRecords: StudentProgress[]
  ): LessonProgressData => {
    const progress = progressRecords.find((p) => p.lesson_id === lessonId)

    if (!progress) {
      return {
        lessonId,
        completionPercentage: 0,
        timeSpent: 0,
        status: 'not_started'
      }
    }

    return {
      lessonId,
      completionPercentage: progress.completion_percentage || 0,
      timeSpent: progress.time_spent_minutes || 0,
      status: progress.status,
      score: progress.score
    }
  },

  getProgressSummary: async (userId: number): Promise<ProgressSummary> => {
    const cacheKey = `progress_summary_${userId}`
    const cached = cacheService.get<ProgressSummary>(cacheKey)
    if (cached) return cached

    const [subjects, topics, lessons, progressRecords] = await Promise.all([
      contentService.getSubjects(),
      contentService.getAllTopics(),
      contentService.getAllLessons(),
      progressService.getAllUserProgress(userId)
    ])

    const completedCount = progressRecords.filter((p) => p.status === 'completed').length
    const inProgressCount = progressRecords.filter((p) => p.status === 'in_progress').length
    const totalStudyTime = progressRecords.reduce((sum, p) => sum + (p.time_spent_minutes || 0), 0)

    const subjectProgressList: SubjectProgress[] = []

    for (const subject of subjects) {
      const subjectProgress = progressService.calculateSubjectProgressFromData(
        subject.id,
        subjects,
        topics,
        lessons,
        progressRecords
      )
      subjectProgressList.push(subjectProgress)
    }

    const totalLessons = subjectProgressList.reduce((sum, s) => sum + s.totalLessons, 0)
    const overallProgress = totalLessons > 0
      ? Math.round((completedCount / totalLessons) * 100)
      : 0

    const weakAreas = await progressService.getWeakAreas(userId, subjectProgressList, topics, lessons, progressRecords, subjects)
    const strongAreas = await progressService.getStrongAreas(userId, subjectProgressList, topics, lessons, progressRecords, subjects)

    const result = {
      userId,
      totalLessons,
      completedLessons: completedCount,
      inProgressLessons: inProgressCount,
      notStartedLessons: totalLessons - completedCount - inProgressCount,
      overallProgress,
      totalStudyTime,
      subjects: subjectProgressList,
      weakAreas,
      strongAreas
    }

    cacheService.set(cacheKey, result, CACHE_TTL.MEDIUM)
    return result
  },

  calculateSubjectProgressFromData: (
    subjectId: number,
    subjects: Subject[],
    topics: Topic[],
    lessons: Lesson[],
    progressRecords: StudentProgress[]
  ): SubjectProgress => {
    const subject = subjects.find((s) => s.id === subjectId)
    if (!subject) {
      return {
        subjectId,
        subjectName: '',
        examType: '',
        totalTopics: 0,
        completedTopics: 0,
        totalLessons: 0,
        completedLessons: 0,
        progressPercentage: 0,
        totalStudyTime: 0
      }
    }

    const subjectTopics = topics.filter((t) => t.subject_id === subjectId)
    const subjectLessonIds = subjectTopics.flatMap((t) => lessons.filter((l) => l.topic_id === t.id).map((l) => l.id))
    const subjectLessons = lessons.filter((l) => l.topic_id === subjectId || subjectLessonIds.includes(l.id))
    const subjectProgress = progressRecords.filter((p) => subjectLessonIds.includes(p.lesson_id))

    const completedLessons = subjectProgress.filter((p) => p.status === 'completed').length
    const totalStudyTime = subjectProgress.reduce((sum, p) => sum + (p.time_spent_minutes || 0), 0)

    const completedTopicIds = new Set<number>()
    subjectProgress.forEach((progress) => {
      if (progress.status === 'completed') {
        const lesson = subjectLessons.find((l) => l.id === progress.lesson_id)
        if (lesson) {
          completedTopicIds.add(lesson.topic_id)
        }
      }
    })

    return {
      subjectId,
      subjectName: subject.name,
      examType: subject.exam_type,
      totalTopics: subjectTopics.length,
      completedTopics: completedTopicIds.size,
      totalLessons: subjectLessons.length,
      completedLessons,
      progressPercentage: subjectLessons.length > 0 ? Math.round((completedLessons / subjectLessons.length) * 100) : 0,
      totalStudyTime
    }
  },

  calculateSubjectProgress: async (userId: number, subjectId: number): Promise<SubjectProgress> => {
    const cacheKey = `subject_progress_${userId}_${subjectId}`
    const cached = cacheService.get<SubjectProgress>(cacheKey)
    if (cached) return cached

    const [subject, topics, lessons] = await Promise.all([
      contentService.getSubject(subjectId),
      contentService.getAllTopics(subjectId),
      contentService.getLessonsBySubject(subjectId)
    ])

    const lessonIds = lessons.map((l) => l.id)
    const allProgressRecords = await progressService.getAllUserProgress(userId)
    const progressRecords = allProgressRecords.filter((p) => lessonIds.includes(p.lesson_id))

    const completedLessons = progressRecords.filter((p) => p.status === 'completed').length
    const totalStudyTime = progressRecords.reduce((sum, p) => sum + (p.time_spent_minutes || 0), 0)

    const completedTopicIds = new Set<number>()
    progressRecords.forEach((progress) => {
      if (progress.status === 'completed') {
        const lesson = lessons.find((l) => l.id === progress.lesson_id)
        if (lesson) {
          completedTopicIds.add(lesson.topic_id)
        }
      }
    })

    const result = {
      subjectId,
      subjectName: subject.name,
      examType: subject.exam_type,
      totalTopics: topics.length,
      completedTopics: completedTopicIds.size,
      totalLessons: lessons.length,
      completedLessons,
      progressPercentage: lessons.length > 0 ? Math.round((completedLessons / lessons.length) * 100) : 0,
      totalStudyTime
    }

    cacheService.set(cacheKey, result, CACHE_TTL.MEDIUM)
    return result
  },

  calculateTopicProgress: async (userId: number, topicId: number): Promise<TopicProgress> => {
    const cacheKey = `topic_progress_${userId}_${topicId}`
    const cached = cacheService.get<TopicProgress>(cacheKey)
    if (cached) return cached

    const [topic, lessons] = await Promise.all([
      contentService.getTopic(topicId),
      contentService.getLessons(topicId)
    ])

    if (!topic || lessons.length === 0) {
      return {
        topicId,
        topicName: '',
        subjectId: 0,
        subjectName: '',
        totalLessons: 0,
        completedLessons: 0,
        progressPercentage: 0,
        masteryLevel: 'beginner'
      }
    }

    const lessonIds = lessons.map((l) => l.id)
    const allProgressRecords = await progressService.getAllUserProgress(userId)
    const progressRecords = allProgressRecords.filter((p) => lessonIds.includes(p.lesson_id))
    const completedLessons = progressRecords.filter((p) => p.status === 'completed').length
    const progressPercentage = Math.round((completedLessons / lessons.length) * 100)

    let masteryLevel: 'beginner' | 'intermediate' | 'advanced' = 'beginner'
    if (progressPercentage >= 70) masteryLevel = 'advanced'
    else if (progressPercentage >= 40) masteryLevel = 'intermediate'

    const subject = await contentService.getSubject(topic.subject_id)
    const subjectName = subject?.name || ''

    const result = {
      topicId,
      topicName: topic.name,
      subjectId: topic.subject_id,
      subjectName,
      totalLessons: lessons.length,
      completedLessons,
      progressPercentage,
      masteryLevel
    }

    cacheService.set(cacheKey, result, CACHE_TTL.MEDIUM)
    return result
  },

  calculateLessonProgress: async (userId: number, lessonId: number): Promise<LessonProgressData> => {
    const progressRecords = await progressService.getAllUserProgress(userId)
    return progressService.calculateLessonProgressFromData(lessonId, progressRecords)
  },

  calculateTopicProgressFromData: (
    topicId: number,
    topics: Topic[],
    lessons: Lesson[],
    progressRecords: StudentProgress[],
    subjects: Subject[]
  ): TopicProgress => {
    const topic = topics.find((t) => t.id === topicId)
    const topicLessons = lessons.filter((l) => l.topic_id === topicId)

    if (!topic || topicLessons.length === 0) {
      return {
        topicId,
        topicName: '',
        subjectId: 0,
        subjectName: '',
        totalLessons: 0,
        completedLessons: 0,
        progressPercentage: 0,
        masteryLevel: 'beginner'
      }
    }

    const topicLessonIds = topicLessons.map((l) => l.id)
    const topicProgress = progressRecords.filter((p) => topicLessonIds.includes(p.lesson_id))

    const completedLessons = topicProgress.filter((p) => p.status === 'completed').length
    const progressPercentage = Math.round((completedLessons / topicLessons.length) * 100)

    let masteryLevel: 'beginner' | 'intermediate' | 'advanced' = 'beginner'
    if (progressPercentage >= 70) masteryLevel = 'advanced'
    else if (progressPercentage >= 40) masteryLevel = 'intermediate'

    const subject = subjects.find((s) => s.id === topic.subject_id)
    const subjectName = subject?.name || ''

    return {
      topicId,
      topicName: topic.name,
      subjectId: topic.subject_id,
      subjectName,
      totalLessons: topicLessons.length,
      completedLessons,
      progressPercentage,
      masteryLevel
    }
  },

  updateEnrollmentProgress: async (userId: number, lessonId: number): Promise<void> => {
    const lessonData = await contentService.getLessonByTopicAndSubject(lessonId)

    if (!lessonData || !lessonData.subjectId) {
      return
    }

    const subjectId = lessonData.subjectId

    const courseSubjectsMap = await contentService.getCourseSubjectsMap()
    const courseIds = courseSubjectsMap.get(subjectId) || []

    if (courseIds.length === 0) {
      return
    }

    const allEnrollments = await enrollmentService.getStudentEnrollments(userId)
    const courseIdToEnrollment = new Map<number, StudentEnrollment>()
    for (const enrollment of allEnrollments) {
      courseIdToEnrollment.set(enrollment.course_id, enrollment)
    }

    const allEnrolledCourseIds = Array.from(courseIdToEnrollment.keys())
    const coursesToUpdate = courseIds.filter(id => courseIdToEnrollment.has(id))

    if (coursesToUpdate.length === 0) {
      return
    }

    const courseSubjectIds = new Set<number>()
    for (const [subId, csCourseIds] of courseSubjectsMap.entries()) {
      if (csCourseIds.some(id => allEnrolledCourseIds.includes(id))) {
        courseSubjectIds.add(subId)
      }
    }

    const [allLessons, allTopics] = await Promise.all([
      contentService.getAllLessons(),
      contentService.getAllTopics()
    ])

    const relevantTopics = allTopics.filter((t) => courseSubjectIds.has(t.subject_id))
    const relevantTopicIds = relevantTopics.map((t) => t.id)

    const relevantLessonIds = allLessons
      .filter((l) => relevantTopicIds.includes(l.topic_id))
      .map((l) => l.id)

    const allProgressRecords = await progressService.getAllUserProgress(userId)
    const progressRecords = allProgressRecords.filter((p) => relevantLessonIds.includes(p.lesson_id))

    const updatePromises: Promise<unknown>[] = []

    for (const courseId of coursesToUpdate) {
      const enrollment = courseIdToEnrollment.get(courseId)
      if (!enrollment) continue

      const currentCourseSubjectIds = new Set<number>()
      for (const [subId, csCourseIds] of courseSubjectsMap.entries()) {
        if (csCourseIds.includes(courseId)) {
          currentCourseSubjectIds.add(subId)
        }
      }

      const courseTopics = relevantTopics.filter((t) => currentCourseSubjectIds.has(t.subject_id))
      const courseTopicIds = courseTopics.map((t) => t.id)

      const courseLessonIds = allLessons
        .filter((l) => courseTopicIds.includes(l.topic_id))
        .map((l) => l.id)

      const completedLessons = progressRecords
        .filter((p) => courseLessonIds.includes(p.lesson_id) && p.status === 'completed')
        .length

      const totalLessons = courseLessonIds.length
      const newProgressPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0

      if (enrollment.progress_percentage !== newProgressPercentage) {
        updatePromises.push(
          enrollmentService.updateProgress(enrollment.id, newProgressPercentage)
        )
      }
    }

    await Promise.all(updatePromises)

    enrollmentService.clearCache(userId)
  },

  getAffectedEnrollmentIds: async (userId: number, lessonId: number): Promise<number[]> => {
    const lessonData = await contentService.getLessonByTopicAndSubject(lessonId)

    if (!lessonData || !lessonData.subjectId) {
      return []
    }

    const courseSubjectsMap = await contentService.getCourseSubjectsMap()
    const courseIds = courseSubjectsMap.get(lessonData.subjectId) || []

    if (courseIds.length === 0) {
      return []
    }

    const allEnrollments = await enrollmentService.getStudentEnrollments(userId)
    const affectedEnrollmentIds = allEnrollments
      .filter((e) => courseIds.includes(e.course_id))
      .map((e) => e.id)

    return affectedEnrollmentIds
  },

  updateEnrollments: async (userId: number, enrollmentIds: number[]): Promise<void> => {
    if (enrollmentIds.length === 0) {
      return
    }

    const [allLessons, allTopics] = await Promise.all([
      contentService.getAllLessons(),
      contentService.getAllTopics()
    ])

    const courseSubjectsMap = await contentService.getCourseSubjectsMap()
    const allEnrollments = await enrollmentService.getStudentEnrollments(userId)
    const courseIdToEnrollment = new Map<number, StudentEnrollment>()
    for (const enrollment of allEnrollments) {
      courseIdToEnrollment.set(enrollment.course_id, enrollment)
    }

    const enrollmentIdToEnrollment = new Map<number, StudentEnrollment>()
    for (const enrollment of allEnrollments) {
      if (enrollmentIds.includes(enrollment.id)) {
        enrollmentIdToEnrollment.set(enrollment.id, enrollment)
      }
    }

    const allEnrolledCourseIds = Array.from(enrollmentIdToEnrollment.values()).map(e => e.course_id)

    const courseSubjectIds = new Set<number>()
    for (const [subId, csCourseIds] of courseSubjectsMap.entries()) {
      if (csCourseIds.some(id => allEnrolledCourseIds.includes(id))) {
        courseSubjectIds.add(subId)
      }
    }

    const relevantTopics = allTopics.filter((t) => courseSubjectIds.has(t.subject_id))
    const relevantTopicIds = relevantTopics.map((t) => t.id)

    const relevantLessonIds = allLessons
      .filter((l) => relevantTopicIds.includes(l.topic_id))
      .map((l) => l.id)

    const allProgressRecords = await progressService.getAllUserProgress(userId)
    const progressRecords = allProgressRecords.filter((p) => relevantLessonIds.includes(p.lesson_id))

    const updatePromises: Promise<unknown>[] = []

    for (const enrollment of enrollmentIdToEnrollment.values()) {
      const courseId = enrollment.course_id

      const currentCourseSubjectIds = new Set<number>()
      for (const [subId, csCourseIds] of courseSubjectsMap.entries()) {
        if (csCourseIds.includes(courseId)) {
          currentCourseSubjectIds.add(subId)
        }
      }

      const courseTopics = relevantTopics.filter((t) => currentCourseSubjectIds.has(t.subject_id))
      const courseTopicIds = courseTopics.map((t) => t.id)

      const courseLessonIds = allLessons
        .filter((l) => courseTopicIds.includes(l.topic_id))
        .map((l) => l.id)

      const completedLessons = progressRecords
        .filter((p) => courseLessonIds.includes(p.lesson_id) && p.status === 'completed')
        .length

      const totalLessons = courseLessonIds.length
      const newProgressPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0

      if (enrollment.progress_percentage !== newProgressPercentage) {
        updatePromises.push(
          enrollmentService.updateProgress(enrollment.id, newProgressPercentage)
        )
      }
    }

    await Promise.all(updatePromises)

    enrollmentService.clearCache(userId)
  },

  updateProgress: async (userId: number, lessonId: number, data: {
    completionPercentage: number
    timeSpent: number
    status?: 'not_started' | 'in_progress' | 'completed'
    score?: number
  }) => {
    console.log('[progressService.updateProgress] Starting', { userId, lessonId, data })

    const existingResult = await easybackClient.list<StudentProgress>('student_progress', {
      filter: {
        user_id: { eq: userId },
        lesson_id: { eq: lessonId }
      },
      limit: 1
    })

    const progressData = {
      user_id: userId,
      lesson_id: lessonId,
      completion_percentage: data.completionPercentage,
      time_spent_minutes: data.timeSpent,
      status: data.status || (data.completionPercentage >= 100 ? 'completed' : 'in_progress'),
      score: data.score,
      last_accessed_at: new Date().toISOString()
    } as Record<string, string | number | boolean | Date | null | undefined>

    let progressId: number | undefined
    if (existingResult.data && existingResult.data.length > 0) {
      progressId = existingResult.data[0].id
      await easybackClient.update<StudentProgress>('student_progress', progressId, progressData)
    } else {
      await easybackClient.insert<StudentProgress>('student_progress', progressData)
    }

    const resultProgress: StudentProgress = {
      id: progressId ?? 0,
      user_id: userId,
      lesson_id: lessonId,
      completion_percentage: data.completionPercentage,
      time_spent_minutes: data.timeSpent,
      status: data.status || (data.completionPercentage >= 100 ? 'completed' : 'in_progress'),
      score: data.score,
      last_accessed_at: new Date().toISOString()
    } 

    const userProgressCacheKey = USER_PROGRESS_CACHE_KEY(userId) 
    const cachedProgress = cacheService.get<StudentProgress[]>(userProgressCacheKey) 

    if (cachedProgress) {
      const updatedProgress = [...cachedProgress]
      const existingIndex = updatedProgress.findIndex(
        (p) => p.user_id === userId && p.lesson_id === lessonId
      )

      if (existingIndex >= 0) {
        updatedProgress[existingIndex] = resultProgress 
      } else {
        updatedProgress.push(resultProgress) 
      }

      cacheService.set(userProgressCacheKey, updatedProgress, CACHE_TTL.MEDIUM) 
    } else {
      console.log('[progressService.updateProgress] No cache to update')
    }

    return resultProgress
  },

  invalidateProgressCache: (userId: number): void => {
    cacheService.invalidate(USER_PROGRESS_CACHE_KEY(userId))
    cacheService.invalidate(`progress_summary_${userId}`)
    cacheService.invalidatePattern(`subject_progress_${userId}_`)
    cacheService.invalidatePattern(`topic_progress_${userId}_`)
  },

  getWeakAreas: async (
    userId: number,
    subjectProgressList?: SubjectProgress[],
    topics?: Topic[],
    lessons?: Lesson[],
    progressRecords?: StudentProgress[],
    subjects?: Subject[]
  ): Promise<WeakArea[]> => {
    if (!subjectProgressList) {
      const summary = await progressService.getProgressSummary(userId)
      subjectProgressList = summary.subjects
      topics = summary.weakAreas.map((w) => ({
        id: w.topicId,
        name: w.topicName,
        subject_id: w.subjectId
      } as Topic))
      lessons = summary.weakAreas.map((w) => ({
        id: w.topicId,
        topic_id: w.topicId
      } as Lesson))
    }

    if (!topics || !lessons || !progressRecords || !subjects) {
      const [fetchedTopics, fetchedLessons, fetchedSubjects] = await Promise.all([
        contentService.getAllTopics(),
        contentService.getAllLessons(),
        contentService.getSubjects()
      ])
      topics = fetchedTopics
      lessons = fetchedLessons
      progressRecords = await progressService.getAllUserProgress(userId)
      subjects = fetchedSubjects
    }

    const weakAreas: WeakArea[] = []

    for (const subjectProgress of subjectProgressList) {
      const subjectTopics = topics.filter((t) => t.subject_id === subjectProgress.subjectId)

      for (const topic of subjectTopics) {
        const topicProgress = progressService.calculateTopicProgressFromData(topic.id, topics, lessons, progressRecords, subjects)
        if (topicProgress.progressPercentage < 70 && topicProgress.totalLessons > 0) {
          weakAreas.push({
            topicId: topic.id,
            topicName: topic.name,
            subjectName: subjectProgress.subjectName,
            subjectId: subjectProgress.subjectId,
            progressPercentage: topicProgress.progressPercentage,
            completedLessons: topicProgress.completedLessons,
            totalLessons: topicProgress.totalLessons
          })
        }
      }
    }

    return weakAreas.sort((a, b) => a.progressPercentage - b.progressPercentage).slice(0, 10)
  },

  getStrongAreas: async (
    userId: number,
    subjectProgressList?: SubjectProgress[],
    topics?: Topic[],
    lessons?: Lesson[],
    progressRecords?: StudentProgress[],
    subjects?: Subject[]
  ): Promise<StrongArea[]> => {
    if (!subjectProgressList) {
      const summary = await progressService.getProgressSummary(userId)
      subjectProgressList = summary.subjects
      topics = summary.strongAreas.map((s) => ({
        id: s.topicId,
        name: s.topicName,
        subject_id: s.subjectId
      } as Topic))
      lessons = summary.strongAreas.map((s) => ({
        id: s.topicId,
        topic_id: s.topicId
      } as Lesson))
    }

    if (!topics || !lessons || !progressRecords || !subjects) {
      const [fetchedTopics, fetchedLessons, fetchedSubjects] = await Promise.all([
        contentService.getAllTopics(),
        contentService.getAllLessons(),
        contentService.getSubjects()
      ])
      topics = fetchedTopics
      lessons = fetchedLessons
      progressRecords = await progressService.getAllUserProgress(userId)
      subjects = fetchedSubjects
    }

    const strongAreas: StrongArea[] = []

    for (const subjectProgress of subjectProgressList) {
      const subjectTopics = topics.filter((t) => t.subject_id === subjectProgress.subjectId)

      for (const topic of subjectTopics) {
        const topicProgress = progressService.calculateTopicProgressFromData(topic.id, topics, lessons, progressRecords, subjects)
        if (topicProgress.progressPercentage >= 80 && topicProgress.totalLessons > 0) {
          strongAreas.push({
            topicId: topic.id,
            topicName: topic.name,
            subjectName: subjectProgress.subjectName,
            subjectId: subjectProgress.subjectId,
            progressPercentage: topicProgress.progressPercentage,
            completedLessons: topicProgress.completedLessons,
            totalLessons: topicProgress.totalLessons
          })
        }
      }
    }

    return strongAreas.sort((a, b) => b.progressPercentage - a.progressPercentage).slice(0, 10)
  },

  getOverallProgress: async (userId: number): Promise<number> => {
    const summary = await progressService.getProgressSummary(userId)
    return summary.overallProgress
  },

  getAffectedCourses: async (_userId: number, lessonId: number): Promise<number[]> => {
    const lessonData = await contentService.getLessonByTopicAndSubject(lessonId)

    if (!lessonData || !lessonData.subjectId) {
      return []
    }

    const courseSubjectsMap = await contentService.getCourseSubjectsMap()
    return courseSubjectsMap.get(lessonData.subjectId) || []
  },

  invalidateContentCache: (): void => {
    cacheService.invalidate('course_subjects_map')
  }
}
